import * as React from 'react'
import { Alert, Box, Button, Container, Divider } from '@mui/material'
import TitleBar from 'components/TitleBar'
import Filters from './components/Filters'
import ScheduleForm from './components/ScheduleForm'
import ScheduleList from './components/ScheduleList'
import useFetchWithAuth from 'hooks/useFetchWithAuth'
import { LoadingContext } from 'contexts/loadingContext'
import { ErrorContext } from 'contexts/errorContext'

/**
 * Schedules component for displaying scheduled orders
 */
const Schedules = () => {
  const [orders, setOrders] = React.useState([])
  const [filters, setFilters] = React.useState({ search: '', day: '-1' })
  const [scheduleToEdit, setScheduleToEdit] = React.useState(null)
  const [scheduleFormOpen, setScheduleFormOpen] = React.useState(false)
  const { setLoading } = React.useContext(LoadingContext)
  const { showError } = React.useContext(ErrorContext)
  const fetchWithAuth = useFetchWithAuth()

  // Fetching orders
  const fetchOrders = async () => {
    setLoading(true)

    try {
      const params = new URLSearchParams({ scheduled: true })
      const response = await fetchWithAuth(`/api/orders?${params}`)
      const data = await response.json()
      setOrders(data)
    } catch (error) {
      showError('Failed to fetch schedules. Please try again or refresh the page.')
      console.error(error.message)
    } finally {
      setLoading(false)
    }
  }

  React.useEffect(() => {
    fetchOrders()
  }, [])

  // Filtering data
  const filteredOrders = orders.filter((order) => {
    const result = []

    if (filters.search !== '') {
      const search = filters.search.toLowerCase()
      result.push(order.customerName.toLowerCase().includes(search) || order.orderNo.toLowerCase().includes(search))
    }

    if (filters.day !== '-1') {
      console.log(order.nextDeliveryDate)
      result.push((new Date(order.nextDeliveryDate)).getUTCDay() === parseInt(filters.day))
    }
    
    return result.every(Boolean)
  })

  // Handles create action
  const handleCreateAction = () => {
    setScheduleToEdit(null)
    setScheduleFormOpen(true)
  }

  // Handles edit action
  const handleEditAction = (order) => {
    setScheduleToEdit(order)
    setScheduleFormOpen(true)
  }

  // Handles delete action
  const handleDeleteAction = (order) => {
    deleteSchedule(order._id)
  }

  const deleteSchedule = async (orderId) => {
    try {
      await fetchWithAuth(`/api/orders/${orderId}/schedule`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ interval: '' })
      })

      setOrders(currentOrders => currentOrders.filter(order => order._id !== orderId))
    } catch (error) {
      showError('Failed to delete schedule. Please try again or refresh the page.')
      console.error(error.message)
    }
  }

  return (
    <Box>
      <TitleBar title="Schedules" actions={[
        <Button variant="contained" onClick={(e) => handleCreateAction()}>Create</Button>
      ]} />
      <Container maxWidth="xl">
        <Filters filters={filters} setFilters={setFilters} />
        <Divider />
        {filteredOrders.length > 0 ? (
          <ScheduleList orders={filteredOrders} editSchedule={handleEditAction} deleteSchedule={handleDeleteAction} />
        ) : (
          <Alert severity="warning" sx={{ my: 6, border: 1, borderColor: 'warning.light' }}>
            No orders found.
          </Alert>
        )}
      </Container>
      
      <ScheduleForm open={scheduleFormOpen} order={scheduleToEdit} onSuccess={fetchOrders} onClose={() => setScheduleFormOpen(false)} />
    </Box>
  )
}

export default Schedules
